import { useState, useMemo, useEffect, useRef } from 'react';
import { Download, Calendar, Filter, ChevronDown, ChevronRight, ArrowLeft, Target, Edit2, Check, X, CheckCircle2, XCircle, User, FileText } from 'lucide-react';
import { Button } from '../../ui/button';
import * as XLSX from 'xlsx';

interface Project {
  id: string;
  name: string;
  number: string;
  dateCreated: string;
  tasks: ProjectTask[];
}

interface ProjectTask {
  id: string;
  title: string;
  categoryId: string;
  category?: string;
  goal?: number;
  questions?: TaskQuestion[];
  selectedQuestionIds?: string[];
  assignedTo?: {
    operators?: boolean;
    vendors?: string[];
    supervisors?: boolean | string[];
  };
}

interface TaskQuestion {
  id: string;
  type: 'number' | 'yes-no' | 'text' | 'multiple-choice' | 'checkbox' | 'dropdown';
  question: string;
  options?: string[];
  goal?: number;
  conditionalOn?: {
    questionId: string;
    value: string;
  };
}

interface TaskSubmission {
  id: string;
  taskId: string;
  projectId: string;
  date: string;
  responses: Record<string, any>;
  submittedBy: string;
  submittedAt: string;
  notes?: string;
}

interface ProjectTimelineViewProps {
  project: Project;
  onBack: () => void;
  onUpdateProject?: (project: Project) => void;
  submissions?: TaskSubmission[];
  categories?: Array<{ id: string; name: string }>;
}

interface DailyReportDetail {
  taskId: string;
  taskName: string;
  date: string;
  value: number;
  submittedBy: string;
  submittedAt: string;
  notes?: string;
  attachments?: string[];
}

interface CategoryWithExpanded {
  id: string;
  name: string;
  expanded: boolean;
}

type TimeframeMode = 'today' | 'week' | 'month' | 'custom';

export function ProjectTimelineView({ project, onBack, onUpdateProject, submissions, categories = [] }: ProjectTimelineViewProps) {
  const getLocalDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Safety check for project data
  if (!project || !project.tasks || project.tasks.length === 0) {
    return (
      <div className="h-screen flex flex-col bg-gray-50">
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Projects</span>
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Tasks Found</h2>
            <p className="text-gray-600">This project doesn't have any tasks yet.</p>
          </div>
        </div>
      </div>
    );
  }

  // Helper function to get vendor name from vendorId
  const getVendorName = (vendorId: string): string => {
    if (vendorId === 'gama-operator') return 'Gama Operator';
    
    // Try to get from sessionStorage (where PIN codes are stored)
    try {
      const pinCodesStr = sessionStorage.getItem('pinCodes');
      if (pinCodesStr) {
        const pinCodes = JSON.parse(pinCodesStr);
        const matchedPin = pinCodes.find((p: any) => p.vendorId === vendorId);
        if (matchedPin?.vendorName) return matchedPin.vendorName;
      }
    } catch (e) {
      console.error('Error reading vendor names:', e);
    }
    
    // Fallback: Try to make vendorId more readable
    // e.g., "vendor-1" -> "Vendor 1"
    if (vendorId.startsWith('vendor-')) {
      const num = vendorId.split('-')[1];
      return `Vendor ${num}`;
    }
    
    return vendorId;
  };

  // Helper function to render role assignment badges
  const renderAssignmentBadges = (task: ProjectTask) => {
    if (!task.assignedTo) return null;
    
    const badges: JSX.Element[] = [];
    
    // Operators badge
    if (task.assignedTo.operators) {
      badges.push(
        <span 
          key="operators" 
          className="inline-flex items-center justify-center w-6 h-6 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full cursor-help hover:bg-orange-200 transition-colors"
          onMouseEnter={(e) => setHoveredBadge({ label: 'Operators', x: e.clientX, y: e.clientY })}
          onMouseLeave={() => setHoveredBadge(null)}
        >
          O
        </span>
      );
    }
    
    // Supervisors badge
    if (task.assignedTo.supervisors) {
      badges.push(
        <span 
          key="supervisors" 
          className="inline-flex items-center justify-center w-6 h-6 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full cursor-help hover:bg-purple-200 transition-colors"
          onMouseEnter={(e) => setHoveredBadge({ label: 'Supervisors', x: e.clientX, y: e.clientY })}
          onMouseLeave={() => setHoveredBadge(null)}
        >
          S
        </span>
      );
    }
    
    // Vendors badges
    if (task.assignedTo.vendors && task.assignedTo.vendors.length > 0) {
      task.assignedTo.vendors.forEach((vendorId, idx) => {
        const vendorName = getVendorName(vendorId);
        const initial = vendorName.charAt(0).toUpperCase();
        badges.push(
          <span 
            key={`vendor-${idx}`} 
            className="inline-flex items-center justify-center w-6 h-6 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full cursor-help hover:bg-emerald-200 transition-colors"
            onMouseEnter={(e) => setHoveredBadge({ label: vendorName, x: e.clientX, y: e.clientY })}
            onMouseLeave={() => setHoveredBadge(null)}
          >
            {initial}
          </span>
        );
      });
    }
    
    if (badges.length === 0) return null;
    
    return (
      <div className="flex items-center gap-1 ml-auto">
        {badges}
      </div>
    );
  };

  // Group tasks by category
  const categoriesMap = new Map<string, CategoryWithExpanded>();
  project.tasks.forEach(task => {
    if (!categoriesMap.has(task.categoryId)) {
      // Look up category name from categories prop
      const categoryName = categories.find(c => c.id === task.categoryId)?.name || task.category || task.categoryId;
      categoriesMap.set(task.categoryId, {
        id: task.categoryId,
        name: categoryName,
        expanded: true,
      });
    }
  });
  const [categoriesState, setCategories] = useState<CategoryWithExpanded[]>(Array.from(categoriesMap.values()));
  
  const [timeframe, setTimeframe] = useState<TimeframeMode>('today');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [selectedCell, setSelectedCell] = useState<{ taskId: string; date: string; questionId: string } | null>(null);
  const [hoveredCell, setHoveredCell] = useState<{ taskId: string; date: string; value: number; x: number; y: number } | null>(null);
  const [editingGoal, setEditingGoal] = useState<{ taskId: string; questionId: string } | null>(null);
  const [goalValue, setGoalValue] = useState<string>('');
  const timelineRef = useRef<HTMLDivElement>(null);
  const [hoveredBadge, setHoveredBadge] = useState<{ label: string; x: number; y: number } | null>(null);

  // Generate date range based on timeframe
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const generateDateRange = (): Date[] => {
    const dates: Date[] = [];
    const seenDates = new Set<string>();
    
    if (timeframe === 'custom') {
      if (customStartDate && customEndDate) {
        const [startY, startM, startD] = customStartDate.split('-').map(Number);
        const [endY, endM, endD] = customEndDate.split('-').map(Number);
        const start = new Date(startY, startM - 1, startD);
        const end = new Date(endY, endM - 1, endD);
        
        if (start <= end) {
          const current = new Date(start);
          while (current <= end) {
            const dateStr = getLocalDateKey(current);
            if (!seenDates.has(dateStr)) {
              seenDates.add(dateStr);
              dates.push(new Date(current));
            }
            current.setDate(current.getDate() + 1);
          }
        }
      }
      return dates;
    }
    
    if (timeframe === 'today') {
      dates.push(new Date(today));
    } else if (timeframe === 'week') {
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        const dateStr = getLocalDateKey(date);
        if (!seenDates.has(dateStr)) {
          seenDates.add(dateStr);
          dates.push(date);
        }
      }
    } else if (timeframe === 'month') {
      const year = today.getFullYear();
      const month = today.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        const dateStr = getLocalDateKey(date);
        if (!seenDates.has(dateStr)) {
          seenDates.add(dateStr);
          dates.push(date);
        }
      }
    }
    
    return dates;
  };
  
  const dates = generateDateRange();

  // Today marker
  const todayString = getLocalDateKey(today);

  const handleTimeframeChange = (mode: TimeframeMode) => {
    setTimeframe(mode);
    if (mode === 'custom') {
      setShowCustomDatePicker(true);
      // Set default dates if not set
      if (!customStartDate || !customEndDate) {
        const end = new Date(today);
        const start = new Date(today);
        start.setDate(start.getDate() - 13);
        setCustomStartDate(getLocalDateKey(start));
        setCustomEndDate(getLocalDateKey(end));
      }
    } else {
      setShowCustomDatePicker(false);
    }
  };

  const handleApplyCustomDates = () => {
    setShowCustomDatePicker(false);
  };

  const toggleCategory = (categoryId: string) => {
    setCategories(categoriesState.map(cat =>
      cat.id === categoryId ? { ...cat, expanded: !cat.expanded } : cat
    ));
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateKey = (date: Date) => {
    return getLocalDateKey(date);
  };

  const getTaskValue = (task: ProjectTask, date: Date) => {
    const dateKey = formatDateKey(date);
    // Look up real submissions first - aggregate all submissions for this task+date
    if (submissions && submissions.length > 0) {
      const subs = submissions.filter(
        s => s.taskId === task.id && s.date === dateKey && s.projectId === project.id
      );
      if (subs.length > 0) {
        const numQ = task.questions?.find(q => q.type === 'number');
        if (numQ) {
          // Sum all numeric values from all submissions
          const total = subs.reduce((sum, sub) => {
            const val = sub.responses[numQ.id];
            return sum + (val !== undefined ? Number(val) : 0);
          }, 0);
          return total > 0 ? total : undefined;
        }
      }
    }
    return undefined;
  };

  const handleCellClick = (taskId: string, date: Date, questionId: string) => {
    const dateKey = formatDateKey(date);
    setSelectedCell({ taskId, date: dateKey, questionId });
  };

  const handleCellHover = (taskId: string, date: Date, value: number, event: React.MouseEvent) => {
    if (value) {
      setHoveredCell({
        taskId,
        date: formatDateKey(date),
        value,
        x: event.clientX,
        y: event.clientY,
      });
    }
  };

  const handleStartEditGoal = (taskId: string, questionId: string, currentGoal: number | undefined) => {
    setEditingGoal({ taskId, questionId });
    setGoalValue(currentGoal?.toString() || '0');
  };

  const handleSaveGoal = (taskId: string, questionId: string) => {
    if (onUpdateProject) {
      const updatedTasks = project.tasks.map(task =>
        task.id === taskId
          ? { ...task, questions: task.questions?.map(q => q.id === questionId ? { ...q, goal: parseFloat(goalValue) || 0 } : q) }
          : task
      );
      onUpdateProject({
        ...project,
        tasks: updatedTasks,
      });
    }
    setEditingGoal(null);
  };

  const handleCancelEditGoal = () => {
    setEditingGoal(null);
    setGoalValue('');
  };

  const hasNumericQuestion = (task: ProjectTask) => {
    return task.questions?.some(q => q.type === 'number') || false;
  };

  const hasYesNoQuestion = (task: ProjectTask) => {
    return task.questions?.some(q => q.type === 'yes-no') || false;
  };

  const hasTextQuestion = (task: ProjectTask) => {
    return task.questions?.some(q => q.type === 'text') || false;
  };

  const isTextLikeQuestion = (questionType: TaskQuestion['type']) => {
    return ['text', 'multiple-choice', 'dropdown', 'checkbox'].includes(questionType);
  };

  const getTextLikeValue = (task: ProjectTask, questionId: string, date: Date): string | null => {
    const dateKey = formatDateKey(date);
    if (submissions && submissions.length > 0) {
      const sub = submissions.find(
        s => s.taskId === task.id && s.date === dateKey && s.projectId === project.id
      );
      if (sub && sub.responses[questionId] !== undefined) {
        return String(sub.responses[questionId]);
      }
    }
    return null;
  };

  // Calculate total progress from submissions for a task
  const calculateTaskProgress = (task: ProjectTask): number => {
    if (!hasNumericQuestion(task)) return 0;
    let total = 0;
    dates.forEach(date => {
      const value = getTaskValue(task, date);
      if (value !== undefined) {
        total += value;
      }
    });
    return total;
  };

  const getTextValue = (task: ProjectTask, date: Date): string | null => {
    const dateKey = formatDateKey(date);
    // Look up real submissions first
    if (submissions && submissions.length > 0) {
      const sub = submissions.find(
        s => s.taskId === task.id && s.date === dateKey && s.projectId === project.id
      );
      if (sub) {
        const textQ = task.questions?.find(q => q.type === 'text');
        if (textQ && sub.responses[textQ.id] !== undefined) {
          return String(sub.responses[textQ.id]);
        }
      }
    }
    return null;
  };

  // Mock yes/no data - true = yes, false = no
  const getYesNoValue = (task: ProjectTask, date: Date): boolean | null => {
    const dateKey = formatDateKey(date);
    // Look up real submissions first
    if (submissions && submissions.length > 0) {
      const sub = submissions.find(
        s => s.taskId === task.id && s.date === dateKey && s.projectId === project.id
      );
      if (sub) {
        const ynQ = task.questions?.find(q => q.type === 'yes-no');
        if (ynQ && sub.responses[ynQ.id] !== undefined) {
          return Boolean(sub.responses[ynQ.id]);
        }
      }
    }
    return null;
  };

  // Calculate yes/no counts for a task across date range
  const getYesNoCounts = (task: ProjectTask) => {
    let yesCount = 0;
    let noCount = 0;
    dates.forEach(date => {
      const value = getYesNoValue(task, date);
      if (value === true) yesCount++;
      if (value === false) noCount++;
    });
    return { yesCount, noCount };
  };

  const mockDailyDetail: DailyReportDetail | null = selectedCell
    ? (() => {
        const task = project.tasks.find(t => t.id === selectedCell.taskId);
        if (!task) return null;
        
        // Get ALL submissions for this task+date
        const allSubs = submissions?.filter(
          s => s.taskId === selectedCell.taskId && s.date === selectedCell.date && s.projectId === project.id
        ) || [];
        
        if (allSubs.length === 0) return null;
        
        const numQ = task.questions?.find(q => q.type === 'number');
        const textQ = task.questions?.find(q => q.type === 'text');
        
        // Calculate total for numeric questions
        let totalValue = 0;
        if (numQ) {
          totalValue = allSubs.reduce((sum, sub) => {
            const val = sub.responses[numQ.id];
            return sum + (val !== undefined ? Number(val) : 0);
          }, 0);
        }
        
        // Use first submission for non-aggregatable data (backward compatibility)
        const firstSub = allSubs[0];
        const textValue = textQ && firstSub ? String(firstSub.responses[textQ.id]) : null;
        
        return {
          taskId: selectedCell.taskId,
          taskName: task.title,
          date: selectedCell.date,
          value: totalValue,
          submittedBy: firstSub?.submittedBy || 'Unknown',
          submittedAt: firstSub?.submittedAt || selectedCell.date,
          notes: textValue || firstSub?.notes,
        };
      })()
    : null;

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    const worksheetData: any[][] = [];

    // Add header row
    worksheetData.push(['Category', 'Task', 'Date', 'Value', 'Submitted By', 'Submitted At', 'Notes']);

    // Add data rows
    categoriesState.forEach(category => {
      const categoryTasks = project.tasks.filter(t => t.categoryId === category.id);
      categoryTasks.forEach(task => {
        dates.forEach(date => {
          const dateKey = formatDateKey(date);
          const value = getTaskValue(task, date);
          const yesNoValue = hasYesNoQuestion(task) ? getYesNoValue(task, date) : null;
          const textQuestion = task.questions?.find(q => isTextLikeQuestion(q.type));
          const textValue = textQuestion ? getTextLikeValue(task, textQuestion.id, date) : null;
          const submittedBy = submissions?.find(
            s => s.taskId === task.id && s.date === dateKey && s.projectId === project.id
          )?.submittedBy || 'Unknown';
          const submittedAt = submissions?.find(
            s => s.taskId === task.id && s.date === dateKey && s.projectId === project.id
          )?.submittedAt || dateKey;
          const notes = textValue || submissions?.find(
            s => s.taskId === task.id && s.date === dateKey && s.projectId === project.id
          )?.notes;

          if (value !== undefined || yesNoValue !== null || textValue !== null) {
            worksheetData.push([
              category.name,
              task.title,
              dateKey,
              value ?? (yesNoValue !== null ? (yesNoValue ? 'Yes' : 'No') : textValue),
              submittedBy,
              submittedAt,
              notes,
            ]);
          }
        });
      });
    });

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Project Timeline');
    XLSX.writeFile(workbook, `${project.name}_Timeline.xlsx`);
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
            <div className="flex items-center gap-4 mt-1.5">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Project No.</span>
                <span className="text-sm font-bold text-gray-900 font-mono bg-gray-100 px-2 py-0.5 rounded">{project.number}</span>
              </div>
              <span className="text-gray-300">|</span>
              <span className="text-sm text-gray-500 flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Created {new Date(project.dateCreated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
              {(['today', 'week', 'month', 'custom'] as TimeframeMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => handleTimeframeChange(mode)}
                  className={`px-4 py-1.5 rounded-md font-medium text-sm transition ${
                    timeframe === mode
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>
            {showCustomDatePicker && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-32 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-32 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                />
                <button
                  onClick={handleApplyCustomDates}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium"
                >
                  Apply
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition">
              <Filter className="w-4 h-4" />
              Filter
            </button>
            <Button
              onClick={exportToExcel}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              <Download className="w-4 h-4" />
              Export
            </Button>
          </div>
        </div>
      </div>

      {/* Unified Scrollable Table */}
      <div className="flex-1 overflow-auto" ref={timelineRef}>
        <div className="inline-block min-w-full">
          {/* Sticky Header Row */}
          <div className="sticky top-0 z-20 flex bg-gray-50 border-b border-gray-200">
            {/* Left header cell */}
            <div className="sticky left-0 z-30 w-[420px] flex-shrink-0 bg-gray-50 border-r border-gray-200 flex items-center px-4 py-3">
              <div className="font-semibold text-base text-gray-800">Categories & Tasks</div>
            </div>
            {/* Date columns */}
            {dates.map((date, idx) => {
              const dateKey = formatDateKey(date);
              const isToday = dateKey === todayString;
              return (
                <div
                  key={`${dateKey}-${idx}`}
                  className={`flex-shrink-0 w-24 px-2 py-3 text-center border-r border-gray-200 ${
                    isToday ? 'bg-indigo-50' : ''
                  }`}
                >
                  <div className="text-xs text-gray-600">{date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                  <div className={`font-semibold ${isToday ? 'text-indigo-600' : 'text-gray-900'}`}>
                    {formatDate(date).split(' ')[1]}
                  </div>
                  <div className="text-xs text-gray-500">{formatDate(date).split(' ')[0]}</div>
                </div>
              );
            })}
          </div>

          {/* Category + Task Rows */}
          {categoriesState.map((category) => {
            const categoryTasks = project.tasks.filter(t => t.categoryId === category.id);
            return (
              <div key={category.id}>
                {/* Category Row */}
                <div className="flex border-b border-gray-200 bg-gray-50" style={{ height: '48px' }}>
                  {/* Left: category toggle */}
                  <div className="sticky left-0 z-10 w-[420px] flex-shrink-0 bg-gray-50 border-r border-gray-200">
                    <button
                      onClick={() => toggleCategory(category.id)}
                      className="w-full h-full flex items-center gap-2 px-4 hover:bg-gray-100 transition"
                    >
                      {category.expanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      )}
                      <span className="font-semibold text-gray-900 text-base">{category.name}</span>
                      <span className="text-xs text-gray-500 ml-auto">{categoryTasks.length} tasks</span>
                    </button>
                  </div>
                  {/* Right: empty date cells */}
                  {dates.map((date, idx) => {
                    const dateKey = formatDateKey(date);
                    const isToday = dateKey === todayString;
                    return (
                      <div
                        key={`${category.id}-${dateKey}-${idx}`}
                        className={`flex-shrink-0 w-24 border-r relative ${
                          isToday ? 'bg-indigo-50/30 border-l-2 border-r-2 border-indigo-400' : 'border-gray-200'
                        }`}
                      />
                    );
                  })}
                </div>

                {/* Task Rows with Questions */}
                {category.expanded && categoryTasks.map((task) => {
                  const questions = task.questions || [];

                  return (
                    <div key={task.id}>
                      {/* Task Sub-Header Row */}
                      <div className="flex border-b border-gray-200 bg-gray-100/50" style={{ height: '40px' }}>
                        <div
                          className="sticky left-0 z-10 w-[420px] flex-shrink-0 bg-gray-100/50 border-r border-gray-200 flex items-center px-4 pl-8"
                          style={{ height: '40px' }}
                        >
                          <span className="text-sm font-semibold text-gray-800">{task.title}</span>
                          {renderAssignmentBadges(task)}
                        </div>
                        {/* Empty date cells for task header */}
                        {dates.map((date, idx) => {
                          const dateKey = formatDateKey(date);
                          const isToday = dateKey === todayString;
                          return (
                            <div
                              key={`${task.id}-header-${dateKey}-${idx}`}
                              className={`flex-shrink-0 w-24 border-r ${
                                isToday ? 'bg-indigo-50/30 border-l-2 border-r-2 border-indigo-400' : 'border-gray-200'
                              }`}
                            />
                          );
                        })}
                      </div>

                      {/* Question Rows */}
                      {questions.map((question, qIdx) => {
                        // Only show selected questions
                        if (!task.selectedQuestionIds?.includes(question.id)) return null;

                        const isNumeric = question.type === 'number';
                        const isYesNo = question.type === 'yes-no';
                        const isTextLike = isTextLikeQuestion(question.type);
                        const rowHeight = 52;
                        
                        // Truncate question text if longer than 50 characters
                        const truncatedQuestion = question.question.length > 50 
                          ? question.question.substring(0, 50) + '...' 
                          : question.question;

                        // Get yes/no counts for this specific question
                        const getQuestionYesNoCounts = () => {
                          if (!isYesNo || !submissions) return { yesCount: 0, noCount: 0 };
                          let yesCount = 0;
                          let noCount = 0;
                          submissions.forEach(sub => {
                            if (sub.taskId === task.id && sub.projectId === project.id) {
                              const response = sub.responses[question.id];
                              if (response === true || response === 'yes') yesCount++;
                              else if (response === false || response === 'no') noCount++;
                            }
                          });
                          return { yesCount, noCount };
                        };

                        const { yesCount, noCount } = isYesNo ? getQuestionYesNoCounts() : { yesCount: 0, noCount: 0 };

                        // Calculate progress for numeric questions
                        const calculateQuestionProgress = (): number => {
                          if (!isNumeric) return 0;
                          let total = 0;
                          dates.forEach(date => {
                            const dateKey = formatDateKey(date);
                            // Get ALL submissions for this task+date, not just one
                            const allSubmissions = submissions?.filter(
                              s => s.taskId === task.id && s.projectId === project.id && s.date === dateKey
                            ) || [];
                            // Sum values from all submissions
                            allSubmissions.forEach(submission => {
                              const value = submission.responses[question.id];
                              if (value != null) {
                                total += Number(value);
                              }
                            });
                          });
                          return total;
                        };

                        const questionProgress = isNumeric ? calculateQuestionProgress() : 0;
                        const questionGoal = question.goal || 0;
                        const isConditional = Boolean(question.conditionalOn);

                        return (
                          <div key={`${task.id}-${question.id}-${qIdx}`} className="flex border-b border-gray-200" style={{ height: `${rowHeight}px` }}>
                            {/* Left: question text */}
                            <div
                              className={`sticky left-0 z-10 w-[420px] flex-shrink-0 bg-white border-r border-b border-gray-200 flex items-center px-4 ${
                                isConditional ? 'pl-20' : 'pl-12'
                              }`}
                              style={{ height: `${rowHeight}px` }}
                              title={question.question}
                            >
                              <div className="flex items-center gap-2 w-full">
                                <span className="text-sm text-gray-700 flex-1">{truncatedQuestion}</span>

                                {/* Numeric Question - Goal Display and Progress */}
                                {isNumeric && questionGoal > 0 && (
                                  <div className="flex items-center gap-2">
                                    {editingGoal?.taskId === task.id && editingGoal?.questionId === question.id ? (
                                      // Edit Mode
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="number"
                                          value={goalValue}
                                          onChange={(e) => setGoalValue(e.target.value)}
                                          className="w-20 px-2 py-1 text-sm border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                                          placeholder="0"
                                          min="0"
                                          autoFocus
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleSaveGoal(task.id, question.id);
                                            else if (e.key === 'Escape') handleCancelEditGoal();
                                          }}
                                        />
                                        <button onClick={() => handleSaveGoal(task.id, question.id)} className="p-1 hover:bg-green-100 rounded transition" title="Save">
                                          <Check className="w-3.5 h-3.5 text-green-600" />
                                        </button>
                                        <button onClick={handleCancelEditGoal} className="p-1 hover:bg-gray-200 rounded transition" title="Cancel">
                                          <X className="w-3.5 h-3.5 text-gray-600" />
                                        </button>
                                      </div>
                                    ) : (
                                      // Display Mode
                                      <div className="flex flex-col gap-1 min-w-[120px]">
                                        <div className="flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded group">
                                          <Target className="w-3 h-3" />
                                          <span>Goal: {questionGoal}</span>
                                          <button
                                            onClick={() => handleStartEditGoal(task.id, question.id, questionGoal)}
                                            className="ml-1 p-0.5 hover:bg-indigo-200 rounded transition opacity-0 group-hover:opacity-100"
                                            title="Edit goal"
                                          >
                                            <Edit2 className="w-3 h-3 text-indigo-600" />
                                          </button>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="flex-1 bg-gray-200 rounded-full h-1">
                                            <div
                                              className="bg-indigo-600 h-1 rounded-full transition-all"
                                              style={{ width: `${Math.min(100, (questionProgress / questionGoal) * 100)}%` }}
                                            />
                                          </div>
                                          <span className="text-xs text-gray-600 whitespace-nowrap">
                                            {questionProgress}/{questionGoal}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Yes/No Question - Show Counts */}
                                {isYesNo && (
                                  <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2 py-1 rounded">
                                      <CheckCircle2 className="w-3 h-3" />
                                      <span>{yesCount}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-xs bg-red-50 text-red-700 px-2 py-1 rounded">
                                      <XCircle className="w-3 h-3" />
                                      <span>{noCount}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Right: date cells with values */}
                            {dates.map((date, idx) => {
                              const dateKey = formatDateKey(date);
                              const isToday = dateKey === todayString;
                              
                              // Get ALL submissions for this task and date (not just one!)
                              const allSubmissionsForDate = submissions?.filter(
                                s => s.taskId === task.id && s.projectId === project.id && s.date === dateKey
                              ) || [];
                              
                              // Aggregate values based on question type
                              let numericValue: number | null = null;
                              let yesNoValue: boolean | null = null;
                              let textValue: string | null = null;
                              
                              if (isNumeric && allSubmissionsForDate.length > 0) {
                                // For numeric: SUM all values
                                const total = allSubmissionsForDate.reduce((sum, sub) => {
                                  const val = sub.responses[question.id];
                                  return sum + (val !== undefined && val !== null ? Number(val) : 0);
                                }, 0);
                                numericValue = total > 0 ? total : null;
                              } else if (isYesNo && allSubmissionsForDate.length > 0) {
                                // For yes/no: show if ANY submission has yes (or we could count)
                                const firstSubmission = allSubmissionsForDate[0];
                                const val = firstSubmission.responses[question.id];
                                yesNoValue = val != null ? (val === true || val === 'yes') : null;
                              } else if (isTextLike && allSubmissionsForDate.length > 0) {
                                // For text: show the first one
                                const firstSubmission = allSubmissionsForDate[0];
                                const val = firstSubmission.responses[question.id];
                                textValue = val ? String(val) : null;
                              }
                              
                              const truncatedText = textValue && textValue.length > 40 ? textValue.substring(0, 40) + '...' : textValue;
                              const hasValue = numericValue !== null || yesNoValue !== null || textValue !== null;

                              return (
                                <div
                                  key={`${task.id}-${question.id}-${dateKey}-${idx}`}
                                  className={`flex-shrink-0 w-24 border-r relative flex items-center justify-center ${
                                    isToday ? 'bg-indigo-50/30 border-l-2 border-r-2 border-indigo-400' : 'border-gray-200'
                                  }`}
                                  style={{ height: `${rowHeight}px` }}
                                  onClick={() => {
                                    if (hasValue) handleCellClick(task.id, date, question.id);
                                  }}
                                  onMouseEnter={(e) => hasValue && numericValue && handleCellHover(task.id, date, numericValue, e)}
                                  onMouseLeave={() => setHoveredCell(null)}
                                  title={textValue || ''}
                                >
                                  {isNumeric && numericValue !== null && (
                                    <div className="bg-indigo-600 text-white text-sm font-medium px-3 py-1 rounded hover:bg-indigo-700 transition cursor-pointer">
                                      {numericValue}
                                    </div>
                                  )}
                                  {isYesNo && yesNoValue !== null && (
                                    <div className="cursor-pointer">
                                      {yesNoValue ? (
                                        <CheckCircle2 className="w-6 h-6 text-green-500" />
                                      ) : (
                                        <XCircle className="w-6 h-6 text-red-400" />
                                      )}
                                    </div>
                                  )}
                                  {isTextLike && textValue && (
                                    <div className="px-2 py-1 max-w-full overflow-hidden">
                                      <p className="text-xs text-gray-700 text-center break-words cursor-pointer hover:bg-gray-50 rounded p-1">
                                        {truncatedText}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Hover Tooltip */}
      {hoveredCell && (() => {
        const task = project.tasks.find(t => t.id === hoveredCell.taskId);
        return (
          <div
            className="fixed z-50 bg-gray-900 text-white text-sm px-3 py-2 rounded-lg shadow-lg pointer-events-none"
            style={{
              left: hoveredCell.x + 10,
              top: hoveredCell.y + 10,
            }}
          >
            <div className="font-medium">{task?.title}</div>
            <div className="text-gray-300">{hoveredCell.date}</div>
            <div className="font-semibold mt-1">Value: {hoveredCell.value}</div>
          </div>
        );
      })()}

      {/* Badge Hover Tooltip */}
      {hoveredBadge && (
        <div
          className="fixed z-50 bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg pointer-events-none"
          style={{
            left: hoveredBadge.x + 10,
            top: hoveredBadge.y + 10,
          }}
        >
          {hoveredBadge.label}
        </div>
      )}

      {/* Right Drawer - Daily Details */}
      {selectedCell && (() => {
        const task = project.tasks.find(t => t.id === selectedCell.taskId);
        const question = task?.questions?.find(q => q.id === selectedCell.questionId);
        
        if (!task || !question) return null;
        
        // Get ALL submissions for this task+date
        const allSubs = submissions?.filter(
          s => s.taskId === selectedCell.taskId && s.date === selectedCell.date && s.projectId === project.id
        ) || [];
        
        if (allSubs.length === 0) return null;
        
        const isNumeric = question.type === 'number';
        const isYesNo = question.type === 'yes-no';
        const isTextLike = isTextLikeQuestion(question.type);
        
        return (
          <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/20" onClick={() => setSelectedCell(null)} />
            <div className="relative h-full w-[480px] bg-white shadow-xl flex flex-col">
              {/* Drawer Header */}
              <div className="border-b border-gray-200 px-6 py-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-900">{task.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{question.question}</p>
                  </div>
                  <button
                    onClick={() => setSelectedCell(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition flex-shrink-0 ml-2"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
                <p className="text-sm text-gray-500">{selectedCell.date}</p>
              </div>

              {/* Drawer Content */}
              <div className="min-h-0 flex-1 overflow-y-auto p-6 space-y-6">
                {/* NUMERIC QUESTION */}
                {isNumeric && (() => {
                  // Calculate total
                  const total = allSubs.reduce((sum, sub) => {
                    const val = sub.responses[question.id];
                    return sum + (val !== undefined && val !== null ? Number(val) : 0);
                  }, 0);
                  
                  // Extract unit from question text (e.g., "Length (meters)" -> "meters")
                  const unit = question.question.match(/\(([^)]+)\)/)?.[1] || 'units';
                  
                  return (
                    <>
                      {/* Total Value */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Total Reported Value</label>
                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                          <div className="text-4xl font-bold text-indigo-900">{total}</div>
                          <div className="text-sm text-indigo-600 mt-1">{unit}</div>
                        </div>
                      </div>
                      
                      {/* Individual Submissions */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Individual Submissions ({allSubs.length})
                        </label>
                        <div className="space-y-3">
                          {allSubs.map((sub, idx) => {
                            const value = sub.responses[question.id];
                            return (
                              <div key={sub.id || idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                  <div className="p-2 bg-indigo-100 rounded-lg flex-shrink-0">
                                    <User className="w-4 h-4 text-indigo-600" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">{sub.submittedBy}</div>
                                    <div className="text-xs text-gray-500 mt-0.5">{sub.submittedAt}</div>
                                    {value !== null && value !== undefined && (
                                      <div className="mt-2 flex items-baseline gap-2">
                                        <span className="text-sm text-gray-600">Value:</span>
                                        <span className="text-lg font-semibold text-indigo-600">{value}</span>
                                        <span className="text-sm text-gray-500">{unit}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  );
                })()}
                
                {/* YES/NO QUESTION */}
                {isYesNo && (() => {
                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Submissions ({allSubs.length})
                      </label>
                      <div className="space-y-3">
                        {allSubs.map((sub, idx) => {
                          const value = sub.responses[question.id];
                          const isYes = value === true || value === 'yes';
                          const isNo = value === false || value === 'no';
                          
                          return (
                            <div key={sub.id || idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                              <div className="flex items-start gap-3">
                                <div className={`p-2 rounded-lg flex-shrink-0 ${isYes ? 'bg-green-100' : 'bg-red-100'}`}>
                                  {isYes ? (
                                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                                  ) : (
                                    <XCircle className="w-4 h-4 text-red-600" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">{sub.submittedBy}</div>
                                  <div className="text-xs text-gray-500 mt-0.5">{sub.submittedAt}</div>
                                  <div className="mt-2">
                                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                                      isYes ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                    }`}>
                                      {isYes ? (
                                        <>
                                          <CheckCircle2 className="w-4 h-4" />
                                          Yes
                                        </>
                                      ) : (
                                        <>
                                          <XCircle className="w-4 h-4" />
                                          No
                                        </>
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                
                {/* TEXT-LIKE QUESTION */}
                {isTextLike && (() => {
                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Submissions ({allSubs.length})
                      </label>
                      <div className="space-y-3">
                        {allSubs.map((sub, idx) => {
                          const value = sub.responses[question.id];
                          const textValue = value ? String(value) : null;
                          
                          return (
                            <div key={sub.id || idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                              <div className="flex items-start gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                                  <FileText className="w-4 h-4 text-blue-600" />
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">{sub.submittedBy}</div>
                                  <div className="text-xs text-gray-500 mt-0.5">{sub.submittedAt}</div>
                                  {textValue && (
                                    <div className="mt-3 bg-white border border-gray-200 rounded-lg p-3">
                                      <p className="text-gray-700 whitespace-pre-wrap break-words">{textValue}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
